import {
  MENTION_PATTERN,
  taskKeyOf,
  taskStatusLabels,
  type AdminChannel,
  type AdminTeamMember,
  type Message,
} from '@nexo/types';
import { Avatar, AvatarStack, Button, EmptyState, SegmentedControl, Spinner, cn } from '@nexo/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Eye,
  Hash,
  Lock,
  MessageSquare,
  MessagesSquare,
  Paperclip,
  SquareCheckBig,
  Users,
  Volume2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api } from '../../lib/api';
import { formatDay, formatLongDate, formatTime } from '../../lib/format';
import i18n from '../../lib/i18n';
import { RichText } from '../chat/RichText';
import { TeamMembersDialog } from './AdminDetails';

type Tab = 'conversations' | 'tasks';

/**
 * TEMPORARY (testing phase): a team opened read-only from /admin, DMs included. The server logs
 * every team and conversation opened; nothing here can write.
 */
export function AdminTeamView() {
  const { t } = useTranslation();
  const { teamId = '' } = useParams();
  const [tab, setTab] = useState<Tab>('conversations');
  const [showMembers, setShowMembers] = useState(false);
  const overview = useQuery({
    queryKey: ['admin', 'inspect', teamId],
    queryFn: () => api.admin.inspectTeam(teamId),
    // Opening the team is logged once, not on every focus.
    staleTime: Infinity,
  });
  const members = useQuery({
    queryKey: ['admin', 'team-members', teamId],
    queryFn: () => api.admin.teamMembers(teamId),
  });

  if (overview.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }
  if (!overview.data) {
    return (
      <EmptyState
        title={t('No se puede abrir este equipo')}
        description={t('No existe o la inspección de equipos está desactivada en el servidor.')}
      />
    );
  }
  const team = overview.data;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 sm:px-6">
        <Link
          to="/admin/teams"
          aria-label={t('Volver a equipos')}
          className="rounded-lg p-1.5 text-muted hover:bg-surface-2 hover:text-text"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <Avatar id={team.id} name={team.name} shape="square" size="sm" />
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold">{team.name}</h1>
          <p className="text-xs text-muted">
            {t('Creado el {{date}}', { date: formatLongDate(team.createdAt) })}
          </p>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-warning-soft px-2.5 py-1 text-xs font-medium text-warning">
          <Eye className="size-3.5" /> {t('Solo lectura · queda registrado en Actividad')}
        </span>
        <span className="flex-1" />
        <Button
          size="sm"
          variant="secondary"
          icon={<Users className="size-4" />}
          onClick={() => setShowMembers(true)}
        >
          {t('Miembros')}
          {members.data ? ` (${members.data.length})` : ''}
        </Button>
        <SegmentedControl<Tab>
          label={t('Sección')}
          size="sm"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'conversations', label: t('Conversaciones'), icon: <MessagesSquare /> },
            { value: 'tasks', label: t('Tareas'), icon: <SquareCheckBig /> },
          ]}
        />
      </header>
      {tab === 'conversations' ? (
        <Conversations channels={team.channels} members={members.data ?? []} />
      ) : (
        <Tasks teamId={team.id} taskKey={team.taskKey} />
      )}
      {showMembers && <TeamMembersDialog team={team} onClose={() => setShowMembers(false)} />}
    </div>
  );
}

const groups: { label: string; match: (c: AdminChannel) => boolean }[] = [
  { label: 'Canales', match: (c) => c.kind === 'channel' && c.mode === 'text' },
  { label: 'Canales de voz', match: (c) => c.kind === 'channel' && c.mode === 'voice' },
  { label: 'Mensajes directos', match: (c) => c.kind === 'dm' },
];

function channelIcon(channel: AdminChannel) {
  if (channel.kind === 'dm') return <MessageSquare />;
  if (channel.mode === 'voice') return <Volume2 />;
  return channel.isPrivate ? <Lock /> : <Hash />;
}

function Conversations({
  channels,
  members,
}: {
  channels: AdminChannel[];
  members: AdminTeamMember[];
}) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(
    channels.find((c) => c.kind === 'channel' && c.mode === 'text')?.id ?? channels[0]?.id ?? null,
  );
  const selected = channels.find((c) => c.id === selectedId);

  return (
    <div className="flex min-h-0 flex-1 max-md:flex-col">
      <nav
        aria-label={t('Conversaciones')}
        className="scrollbar-thin shrink-0 overflow-y-auto border-border p-2 max-md:max-h-48 max-md:border-b md:w-64 md:border-r"
      >
        {groups.map((group) => {
          const list = channels.filter(group.match);
          if (list.length === 0) return null;
          return (
            <div key={group.label} className="mb-3">
              <p className="px-2 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                {t(group.label)}
              </p>
              {list.map((channel) => (
                <button
                  key={channel.id}
                  type="button"
                  aria-current={channel.id === selectedId ? 'true' : undefined}
                  onClick={() => setSelectedId(channel.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm [&_svg]:size-4 [&_svg]:shrink-0',
                    channel.id === selectedId
                      ? 'bg-selected font-medium text-selected-fg'
                      : 'text-muted hover:bg-surface-2 hover:text-text',
                  )}
                >
                  {channelIcon(channel)}
                  <span className="min-w-0 flex-1 truncate">{channel.name}</span>
                  <span className="text-xs text-subtle tabular-nums">{channel.messageCount}</span>
                </button>
              ))}
            </div>
          );
        })}
      </nav>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        {selected ? (
          <Timeline key={selected.id} channel={selected} members={members} />
        ) : (
          <EmptyState title={t('Este equipo no tiene conversaciones')} />
        )}
      </section>
    </div>
  );
}

/** `<@id>` mentions as plain `@Name` (this view has no team context to resolve them). */
function withNames(body: string, members: AdminTeamMember[]) {
  return body.replace(MENTION_PATTERN, (_, id: string) => {
    const name = members.find((m) => m.id === id)?.displayName;
    return name ? `@${name}` : i18n.t('@usuario');
  });
}

function Timeline({ channel, members }: { channel: AdminChannel; members: AdminTeamMember[] }) {
  const { t } = useTranslation();
  const pages = useInfiniteQuery({
    queryKey: ['admin', 'inspect-messages', channel.id],
    queryFn: ({ pageParam }) => api.admin.channelMessages(channel.id, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    staleTime: Infinity,
  });
  // Pages come newest first; each page is already in chronological order.
  const messages = [...(pages.data?.pages ?? [])].reverse().flatMap((p) => p.items);

  return (
    <>
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm [&_svg]:size-4 [&_svg]:text-muted">
        {channelIcon(channel)}
        <span className="font-semibold">{channel.name}</span>
        {channel.description && (
          <span className="truncate text-xs text-muted">{channel.description}</span>
        )}
      </header>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {pages.hasNextPage && (
          <div className="mb-3 flex justify-center">
            <Button
              size="sm"
              variant="secondary"
              loading={pages.isFetchingNextPage}
              onClick={() => void pages.fetchNextPage()}
            >
              {t('Cargar mensajes anteriores')}
            </Button>
          </div>
        )}
        {pages.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState
            title={channel.mode === 'voice' ? t('Canal de voz sin mensajes') : t('Sin mensajes')}
          />
        ) : (
          <ol className="flex flex-col gap-1">
            {messages.map((message, i) => {
              const day = formatDay(message.createdAt);
              const newDay = i === 0 || formatDay(messages[i - 1]?.createdAt ?? '') !== day;
              return (
                <li key={message.id}>
                  {newDay && (
                    <p className="my-3 text-center text-xs font-medium text-subtle">{day}</p>
                  )}
                  <ReadOnlyMessage message={message} members={members} />
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </>
  );
}

function ReadOnlyMessage({
  message,
  members,
  nested = false,
}: {
  message: Message;
  members: AdminTeamMember[];
  nested?: boolean;
}) {
  const { t } = useTranslation();
  const [showThread, setShowThread] = useState(false);
  const replies = useQuery({
    queryKey: ['admin', 'inspect-thread', message.id],
    queryFn: () => api.admin.threadReplies(message.id),
    enabled: showThread,
  });
  return (
    <article
      className={cn('flex gap-3 rounded-lg px-2 py-1.5', !nested && 'hover:bg-surface-2/50')}
    >
      <Avatar
        id={message.author.id}
        name={message.author.displayName}
        src={message.author.avatarUrl}
        size={nested ? 'xs' : 'sm'}
      />
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2 text-sm">
          <span className="font-semibold">{message.author.displayName}</span>
          <span className="text-xs text-subtle">{formatTime(message.createdAt)}</span>
          {message.editedAt && <span className="text-xs text-subtle">{t('(editado)')}</span>}
        </p>
        {message.replyTo && (
          <p className="mt-0.5 border-l-2 border-border pl-2 text-xs text-muted">
            {message.replyTo.author.displayName}:{' '}
            {withNames(message.replyTo.body, members).slice(0, 140)}
          </p>
        )}
        {message.body && (
          <div className="text-sm leading-relaxed break-words whitespace-pre-wrap">
            <RichText text={withNames(message.body, members)} />
          </div>
        )}
        {message.call && <p className="text-xs text-muted">📞 {t('Llamada')}</p>}
        {message.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {message.attachments.map((file) =>
              file.isImage ? (
                <a key={file.id} href={file.url} target="_blank" rel="noreferrer noopener">
                  <img
                    src={file.previewUrl ?? file.url}
                    alt={file.name}
                    className="max-h-40 rounded-lg border border-border"
                  />
                </a>
              ) : (
                <a
                  key={file.id}
                  href={file.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-surface-2"
                >
                  <Paperclip className="size-3.5" /> {file.name}
                </a>
              ),
            )}
          </div>
        )}
        {message.reactions.length > 0 && (
          <p className="mt-1 flex flex-wrap gap-1">
            {message.reactions.map((r) => (
              <span key={r.emoji} className="rounded-full bg-surface-2 px-1.5 text-xs">
                {r.emoji} {r.count}
              </span>
            ))}
          </p>
        )}
        {message.thread && message.thread.replyCount > 0 && !nested && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setShowThread((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
            >
              <AvatarStack people={message.thread.participants} max={3} />
              {message.thread.replyCount === 1
                ? showThread
                  ? t('Ocultar 1 respuesta')
                  : t('Ver 1 respuesta')
                : showThread
                  ? t('Ocultar {{count}} respuestas', { count: message.thread.replyCount })
                  : t('Ver {{count}} respuestas', { count: message.thread.replyCount })}
            </button>
            {showThread && (
              <div className="mt-1 border-l-2 border-border pl-2">
                {replies.isPending ? (
                  <Spinner className="m-2" />
                ) : (
                  replies.data?.map((reply) => (
                    <ReadOnlyMessage key={reply.id} message={reply} members={members} nested />
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function Tasks({ teamId, taskKey }: { teamId: string; taskKey: string }) {
  const { t } = useTranslation();
  const tasks = useQuery({
    queryKey: ['admin', 'inspect-tasks', teamId],
    queryFn: () => api.admin.teamTasks(teamId),
  });
  if (tasks.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }
  const list = tasks.data ?? [];
  if (list.length === 0) return <EmptyState title={t('Este equipo no tiene tareas')} />;
  return (
    <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="sticky top-0 bg-surface-2 text-left text-xs text-muted">
          <tr>
            <Th>{t('Clave')}</Th>
            <Th>{t('Título')}</Th>
            <Th>{t('Estado')}</Th>
            <Th>{t('Responsables')}</Th>
            <Th>{t('Creada por')}</Th>
            <Th>{t('Creada')}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {list.map((task) => (
            <tr key={task.id}>
              <td className="px-3 py-2 font-mono text-xs text-subtle">
                {taskKeyOf(taskKey, task.number)}
              </td>
              <td className={cn('px-3 py-2', task.status === 'done' && 'text-subtle line-through')}>
                {task.title}
              </td>
              <td className="px-3 py-2 text-muted">{t(taskStatusLabels[task.status])}</td>
              <td className="px-3 py-2">
                {task.assignees.length > 0 ? (
                  <AvatarStack people={task.assignees} max={3} />
                ) : (
                  <span className="text-xs text-subtle">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-muted">{task.createdBy.displayName}</td>
              <td className="px-3 py-2 text-muted">{formatLongDate(task.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}
